import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTeams } from '../use-teams';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  addWireDays,
  formatHours,
  parseMinutes,
  personSchema,
  projectSchema,
  taskSchema,
  toWireDate,
  wireMonday,
  metafieldSchema,
  overtimeTypeSchema,
  worklogDayTotalsSchema,
  worklogSchema,
  worklogTaskRowSchema,
  type Metafield,
  type OvertimeType,
  type Worklog,
  type WorklogDayTotals,
  formatSort,
  type SortKey,
  DEFAULT_WORKLOG_RULES,
  worklogRulesSchema,
  type WorklogRules,
} from '@forecastify/shared';
import { Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useTimeSectionHeader } from './time-section-header';
import { useLiveInvalidation } from '../../live/use-live';
import { TIME_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, totalOf, usePermissionSet } from '../permissions-hook';
import { pagerOf, usePages } from '../use-pages';
import { useFirstLoad } from '../use-first-load';
import { bulkTargets, postBulk } from '../use-bulk';
import { EntryDialog, draftFromEntry, draftFromRange, type EntryDraft } from './entry-dialog';
import type { TaskChoice } from './task-search-field';
import { worklogBody } from './worklog-form';
import { LogsScreen } from './logs-screen';
import { useExport } from '../exports/use-export';
import { useImport } from '../imports/use-import';
import type { CalendarRange } from './logs-calendar';
import { billsNowhere } from './bills-nowhere';
import { requestAll } from '../read-all';

/**
 * The Időlogok section — four views over one data set.
 *
 * ⚠️⚠️ **TWO READS, AND THEY ANSWER DIFFERENT QUESTIONS.** The list and the grid want *a page of
 * whatever the filters select*; the timesheet and the calendar want *one week*. Serving both from
 * one request means either the week views get a page of hours (and silently lose a Friday) or the
 * list loses its pager. So there are two: a paged one for the filters, and one for the week.
 *
 * ⚠️⚠️ **31 Oct 2026 · THE WEEK PAGES TOO, BY TASK.** It read every page of the week, which is
 * bounded for one person and is NOT for the workspace list — a week of everybody's hours grows with
 * headcount and passes `read-all.ts`' ceiling. `/worklogs/by-task` pages whole tasks, each with all
 * its hours that week, so a timesheet row is never split; `/worklogs/days` totals the whole week
 * for the column and week figures, which no page can add up.
 */

/** How many timesheet rows — tasks — one week page holds. */
const WEEK_TASK_PAGE = 50;

let toastCounter = 0;
const nextToastId = (): string => `logs-${String((toastCounter += 1))}`;

/**
 * One `details` entry, by its code.
 *
 * ⚠️ **`details` is a LIST and the arms need specific members of it**, so a message cannot just
 * take `details[0]`: `day_over_maximum` carries two entries and reading the wrong one puts the
 * maximum where the total belongs. Rule 5 says clients branch on `code`; this is the same rule one
 * level down.
 */
function detailFor(
  envelope: { error?: { details?: { code?: string; message?: string }[] } } | null,
  code: string,
): string {
  return envelope?.error?.details?.find((entry) => entry.code === code)?.message ?? '';
}

/**
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE BODY, TWO SCOPES — `LogsRoute` and `WorkspaceLogsRoute` below.         │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * `requests-route.tsx` does the same thing for the same reason: the two lists differ in one
 * question — *whose* — and every other line of them is identical.
 */
function Logs({ listScope }: { readonly listScope: 'mine' | 'all' }) {
  /* ⚠️ The mockup's header dropdown for this section — `time-section-header.tsx`. The
     workspace-wide list is not IN the time section, so it does not claim the header. */
  useTimeSectionHeader('logs', listScope === 'mine');
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const t = useT();
  const permissions = usePermissionSet();

  const today = useMemo(() => toWireDate(new Date()), []);

  /**
   * 30 Oct 2026 · C13's rules, so an entry in a locked month (or past the backdating window) is
   * drawn refusing rather than offered and then refused — `entry-lock.ts`. Defaulted, as on the
   * day view: a failed read must not lock every entry, and the server enforces either way.
   */
  const [rules, setRules] = useState<WorklogRules>(DEFAULT_WORKLOG_RULES);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/worklogs/rules').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = worklogRulesSchema.safeParse(await response.json().catch(() => null));
      if (parsed.success) setRules(parsed.data);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [session.workspaceId]);

  const [entries, setEntries] = useState<readonly Worklog[]>([]);
  const [weekEntries, setWeekEntries] = useState<readonly Worklog[]>([]);
  /* The whole week's hours per day — the timesheet's column and week totals. `null` until read. */
  const [weekTotals, setWeekTotals] = useState<WorklogDayTotals | null>(null);
  const [projects, setProjects] = useState<readonly { id: string; name: string }[]>([]);
  /* ⚠️ Item 18 · the people the member filter offers. Loaded beside the projects rather than in
     its own effect — it is reference data with the same lifetime. */
  const [members, setMembers] = useState<readonly { id: string; name: string }[]>([]);
  /**
   * The timesheet's add-row choices: what the menu's search last matched, plus every task already
   * added as a row — a row has to keep resolving its key to an id after the query moves on.
   *
   * ⚠️⚠️ **SEARCHED, 30 Oct 2026.** This was `GET /tasks` — one page of fifty workspace tasks, of
   * which the menu offered ten — so most tasks could not be added at all, and a cell typed into a
   * row whose task was not among the fifty was silently not written.
   */
  const [addableMatches, setAddableMatches] = useState<
    readonly { id: string; key: string; title: string; closed?: boolean }[]
  >([]);
  const [addedTasks, setAddedTasks] = useState<
    readonly { id: string; key: string; title: string }[]
  >([]);
  const tasks = useMemo(
    () => [
      ...addedTasks,
      ...addableMatches.filter((task) => !addedTasks.some((added) => added.id === task.id)),
    ],
    [addedTasks, addableMatches],
  );
  /**
   * The workspace's `worklog` metafields.
   *
   * ⚠️ **Not fatal.** A caller who cannot read them gets the dialog without the custom-field box
   * rather than no dialog at all — the rule every picker feed here follows.
   */
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  /**
   * ⚠️⚠️ **THE ENTRY DIALOG'S TASK SEARCH — feedback round 4 item 21.** The dialog used to pick
   * from `tasks` above: whatever `GET /tasks` returned, which is fifty rows in id order with no way
   * to reach the fifty-first. `time-route.tsx` has searched since C11 and this is the same call.
   */
  const [taskResults, setTaskResults] = useState<readonly TaskChoice[]>([]);
  /**
   * ⚠️ **The overtime types, for the dialog's picker** — item 19: the calendar could not mark an
   * hour as overtime at all. Not fatal: without them the control is absent and an hour is an hour.
   */
  const [overtimeTypes, setOvertimeTypes] = useState<readonly OvertimeType[]>([]);

  /**
   * The tasks a query names — the caller's own when it is empty. One search, two pickers: the
   * entry dialog's and the timesheet's add-row menu.
   */
  const findTasks = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      const params = new URLSearchParams();
      if (trimmed === '') {
        /* ⚠️ The member id is per-WORKSPACE — `session.workspaces` carries one each, because the
           same account is a different member in each. There is no single "my member id". */
        const me = session.workspaces.find((entry) => entry.id === session.workspaceId);
        if (me !== undefined) params.set('assigneeMemberId', me.memberId);
      } else {
        params.set('search', trimmed);
      }

      const response = await session.request(`/tasks?${params.toString()}`).catch(() => null);
      return response?.ok === true ? (parseList(taskSchema, await response.json()) ?? []) : [];
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
    [session.workspaceId],
  );

  const searchTasks = useCallback(
    async (query: string): Promise<void> => {
      const parsed = await findTasks(query);
      setTaskResults(
        parsed.slice(0, 8).map((task) => ({
          id: task.id,
          key: task.key,
          title: task.title,
          projectName: task.projectName,
          projectColor: task.projectColor,
        })),
      );
    },
    [findTasks],
  );

  useEffect(() => {
    void searchTasks('');
  }, [searchTasks]);

  /* The add-row menu's search: debounced, and only the newest answer lands. */
  const addableQuery = useRef({ timer: 0, latest: '' });
  const searchAddable = useCallback(
    (query: string): void => {
      window.clearTimeout(addableQuery.current.timer);
      addableQuery.current.latest = query;
      addableQuery.current.timer = window.setTimeout(
        () => {
          void (async () => {
            const parsed = await findTasks(query);
            if (addableQuery.current.latest !== query) return;
            /* 30 Oct 2026 · a closed task is listed, unpickable, and says why — it used to vanish. */
            setAddableMatches(
              parsed.map((task) => ({
                id: task.id,
                key: task.key,
                title: task.title,
                closed: task.statusCategory === 'closed',
              })),
            );
          })();
        },
        query === '' ? 0 : 250,
      );
    },
    [findTasks],
  );

  useEffect(() => {
    searchAddable('');
  }, [searchAddable]);

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/overtime/types').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(overtimeTypeSchema, await response.json());
      if (parsed !== null) setOvertimeTypes(parsed.filter((type) => type.active));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
  }, [session.workspaceId]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=worklog').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setMetafields(parsed);
    })();
  }, [session]);

  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState('');
  /**
   * WARN2 **WHO LOGGED IT - item 18's second pass.** The toolbar offered one filter, over projects,
   * and called it *Ügyfél*. Of the columns this list actually has - date, task, note, project,
   * person, hours - the person is the other one worth a picker: date has the range views above it,
   * task and note are what the search box searches, and hours is a quantity nobody filters by
   * equality. The server has taken `memberId` since C11 and trims it to the caller's own scope.
   */
  const [memberId, setMemberId] = useState('');
  /* R-60 · whose team — the workspace list only; the personal one is pinned to the reader. */
  const teams = useTeams();
  const [teamId, setTeamId] = useState('');
  const scopedTeamId = listScope === 'mine' ? '' : teamId;
  const [range, setRange] = useState<'all' | 'week' | 'month'>('all');

  /**
   * ⚠️⚠️ **MY OWN MEMBER ID, AND THE PERSONAL LIST IS PINNED TO IT.** Sent as the ordinary
   * `memberId` filter, which the server has taken since C11 — so this is one preset value in a
   * query that already existed, not a second code path.
   *
   * ⚠️⚠️ **IT CANNOT WIDEN ANYTHING AND DOES NOT PRETEND TO.** `worklog.service.ts` applies
   * the caller's `worklog.read` scope AFTER this filter, precisely so a hand-written
   * `?memberId=somebody-else` cannot reach past it. This narrows; only the server decides the
   * ceiling.
   *
   * ⚠️ **Per WORKSPACE** — the same account is a different member in each, so there is no single
   * "my member id" to cache. `searchTasks` above reads it the same way.
   */
  const myMemberId =
    session.workspaces.find((entry) => entry.id === session.workspaceId)?.memberId ?? null;
  /* ⚠️ The filter the reads actually carry: the pin on the personal list, the toolbar's
     picker on the workspace one. */
  const scopedMemberId = listScope === 'mine' ? (myMemberId ?? '') : memberId;

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE DAY, FROM THE ADDRESS — item 3.                              │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The dashboard chart's bars link here as `/time?from=2026-09-03&to=2026-09-03`. Without this
   * the link would land on an unfiltered list — which is worse than not linking at all, because it
   * looks like it worked.
   *
   * ⚠️⚠️ **A FOURTH RANGE, NOT A REUSE OF `week`.** The three named ranges are computed from
   * TODAY (`rangeOf`), so there is nowhere in them to put an arbitrary date; a `day` mode carries
   * its own bounds and the picker simply shows none of the three selected.
   *
   * ⚠️ **Read once, into state.** Reading the address on every render would make the filter
   * unchangeable: picking *this week* would re-apply the day on the next pass.
   */
  const [urlParams] = useSearchParams();
  const [dayRange] = useState<{ from: string; to: string } | null>(() => {
    const from = urlParams.get('from');
    const to = urlParams.get('to');
    return from === null || to === null ? null : { from, to };
  });
  /* 17 Oct 2026 · a period's row menu sends its contract — read once, cleared by its chip. */
  const [contract, setContract] = useState<{ id: string; label: string } | null>(() => {
    const id = urlParams.get('contractId');
    return id === null ? null : { id, label: urlParams.get('contract') ?? id };
  });
  const [weekStart, setWeekStart] = useState(() => wireMonday(toWireDate(new Date())) ?? today);

  /**
   * ⚠️⚠️ **A PAGER, NOT *LOAD MORE* — ITEM 38, AND THE CURSOR STACK IT REPLACES IS GONE.** This
   * list appended: page two was added under page one, and the only way back was to reload the
   * screen. Everything else in the product had already moved to `[<] [selector] [>] [total]` in
   * item 20, and `state-rules.md` §6's *"consistent within a screen"* had become *"consistent
   * everywhere except here"*.
   */
  const pages = usePages();
  const { settle: settlePage } = pages;
  /* ⚠️ The week's own pager — tasks, not hours. Reset below whenever the week or whose it is changes. */
  const weekPages = usePages(WEEK_TASK_PAGE);
  const { settle: settleWeek, reset: resetWeek } = weekPages;
  const [sort, setSort] = useState<readonly SortKey[]>([]);

  /** Timesheet rows somebody added that have nothing logged against them yet. */
  const [extraTaskIds, setExtraTaskIds] = useState<readonly string[]>([]);

  const [draft, setDraft] = useState<EntryDraft | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const exporter = useExport();

  /**
   * ⚠️ **The range views are computed here, not sent as a name.** `from`/`to` is what the endpoint
   * speaks; a `range=week` parameter would put "what a week is" on the server, where it would
   * disagree with the client the first time somebody's week started on a Sunday.
   */
  const listQuery = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (search.trim() !== '') params.set('search', search.trim());
    if (pages.offset > 0) params.set('offset', String(pages.offset));
    /* ⚠️ `worked_on:desc,member:asc` — the ORDER is the priority, mapped through `WORKLOG_SORT`. */
    const encoded = formatSort(sort);
    if (encoded !== undefined) params.set('sort', encoded);
    if (projectId !== '') params.set('projectId', projectId);
    if (scopedMemberId !== '') params.set('memberId', scopedMemberId);
    if (scopedTeamId !== '') params.set('teamId', scopedTeamId);
    /* ⚠️ The address's day wins over the named range, and only until somebody picks one — see
       `onRange` below, which clears it by setting a named range. */
    if (contract !== null) params.set('contractId', contract.id);
    if (dayRange !== null && range === 'all') {
      params.set('from', dayRange.from);
      params.set('to', dayRange.to);
    } else if (range === 'week') {
      const monday = wireMonday(today) ?? today;
      params.set('from', monday);
      params.set('to', addWireDays(monday, 6) ?? monday);
    } else if (range === 'month') {
      params.set('from', `${today.slice(0, 8)}01`);
      params.set('to', today);
    }
    return params;
  }, [
    search,
    projectId,
    scopedMemberId,
    scopedTeamId,
    range,
    today,
    sort,
    pages.offset,
    dayRange,
    contract,
  ]);

  /**
   * C48 · the hours that already carry an overtime claim, so the row menu can say so rather than
   * offer something the server will refuse. 31 Oct 2026 · ⚠️ **read off the hours themselves**
   * (`worklog.overtime`): it was `/overtime?mine=1`, which the list never understood — it read every
   * claim in the workspace, and would now read one page of them. A WITHDRAWN claim does not hold
   * the hour (`overtime.service.ts`), so it is not "claimed" here either.
   */
  const overtimeClaimed = useMemo(
    () =>
      new Set(
        [...entries, ...weekEntries]
          .filter((entry) => entry.overtime !== null && entry.overtime.status !== 'revoked')
          .map((entry) => entry.id),
      ),
    [entries, weekEntries],
  );

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const weekEnd = addWireDays(weekStart, 6) ?? weekStart;
    /* ⚠️⚠️ **THE WEEK READS TAKE THE PIN TOO.** The timesheet and the calendar are fed by THESE
       calls, not the paged list — leaving them unscoped would give the personal section a filtered
       list and a grid of everybody's hours, which is the same bug wearing a different view. */
    const weekQuery = new URLSearchParams({ from: weekStart, to: weekEnd });
    if (scopedMemberId !== '') weekQuery.set('memberId', scopedMemberId);
    if (scopedTeamId !== '') weekQuery.set('teamId', scopedTeamId);
    const weekPage = new URLSearchParams(weekQuery);
    weekPage.set('limit', String(WEEK_TASK_PAGE));
    if (weekPages.offset > 0) weekPage.set('offset', String(weekPages.offset));
    const [page, week, totals] = await Promise.all([
      session.request(`/worklogs?${listQuery.toString()}`).catch(() => null),
      session.request(`/worklogs/by-task?${weekPage.toString()}`).catch(() => null),
      session.request(`/worklogs/days?${weekQuery.toString()}`).catch(() => null),
    ]);

    if (page === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!page.ok) {
      setFailure(await failureOf(page));
      return;
    }

    const body: unknown = await page.json();
    const rows = parseList(worklogSchema, body);
    if (rows === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setEntries(rows);

    settlePage(totalOf(body));

    /* ⚠️ Not fatal, either of them: the list above is the screen's answer, and a week that could
       not be read keeps what it drew rather than replacing the list with an error. */
    if (week?.ok === true) {
      const weekBody: unknown = await week.json().catch(() => null);
      const weekRows = parseList(worklogTaskRowSchema, weekBody);
      if (weekRows !== null) {
        setWeekEntries(weekRows.flatMap((row) => row.entries));
        settleWeek(totalOf(weekBody));
      }
    }
    if (totals?.ok === true) {
      const parsed = worklogDayTotalsSchema.safeParse(await totals.json().catch(() => null));
      setWeekTotals(parsed.success ? parsed.data : null);
    }
    /* ⚠️ `scopedMemberId` is in here for the WEEK reads, which build their query inline — the paged
       one carries it through `listQuery` already. Without it the timesheet would keep last scope's
       rows when the same component serves the other address. */
  }, [
    session,
    listQuery,
    weekStart,
    settlePage,
    settleWeek,
    weekPages.offset,
    scopedMemberId,
    scopedTeamId,
  ]);

  /* ⚠️ A different week, or somebody else's, starts on its first page — `use-pages.ts` on why a
     stale offset past the end draws an empty timesheet with nothing to explain it. */
  useEffect(() => {
    resetWeek();
  }, [weekStart, scopedMemberId, scopedTeamId, resetWeek]);

  /* ⚠️ Declared after `reload`, because the import hook takes it: rows landed behind the dialog, so
   * the list underneath is stale the moment the report appears. */
  const importer = useImport(() => void reload());

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. A colleague's change
   * refetches this in place — no spinner, no blank, no reload. R-19. */
  useLiveInvalidation(TIME_TOPICS, reload);

  /**
   * The pickers. Not fatal — a caller without `project.view` should still see their own hours.
   *
   * ⚠️ **A ref, not a `let`, and TypeScript is the reason.** A `let alive = true` reassigned only
   * inside the cleanup closure is narrowed to `true` at the point it is read, so `if (!alive)` is
   * reported as always-falsy and the guard is dead code that lint can see and the compiler cannot.
   * `hard-won.md` has this one; `task-route.tsx` uses the same shape.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    void (async () => {
      const [projectList, memberList] = await Promise.all([
        requestAll(session, '/projects').catch(() => null),
        requestAll(session, '/people').catch(() => null),
      ]);
      if (!alive.current) return;

      if (projectList?.ok === true) {
        const list = parseList(projectSchema, await projectList.json());
        if (list !== null) {
          setProjects(list.map((project) => ({ id: project.id, name: project.name })));
        }
      }
      if (memberList?.ok === true) {
        const list = parseList(personSchema, await memberList.json());
        if (list !== null) {
          setMembers(
            list
              /* ⚠️ Members only: an invitation row has no member id to filter worklogs by, and
                 nobody has logged an hour against one. */
              .filter((person) => person.kind === 'member')
              .map((person) => ({ id: person.id, name: person.displayName })),
          );
        }
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [session]);

  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  /**
   * ⚠️⚠️ **THE REFUSAL WORDING, IN ONE PLACE, BECAUSE TWO WRITERS NEED IT.** Every branch here
   * used to live inside `write`; `writeJson` below wants the created row and would otherwise carry
   * a second copy of the same fourteen arms. `time-route.tsx` made the same extraction for the
   * same reason.
   */
  const reportRefusal = async (response: Response | null): Promise<void> => {
    if (response === null) {
      setServerError(t('error.unavailable'));
      return;
    }
    const envelope = (await response.json().catch(() => null)) as {
      error?: { code?: string; details?: { code?: string; message?: string }[] };
    } | null;
    const code = envelope?.error?.code ?? 'unavailable';

    /* Branching on `code`, never on `message` — rule 5. Same arms as the overview's. */
    if (code === 'not_a_project_member') setServerError(t('time.error.notAMember'));
    /* 26 Oct 2026 · the person has *Időt rögzít* off — the server refuses their hours. */
    else if (code === 'member_not_tracking') {
      setServerError(
        t('time.error.notLogging', { name: envelope?.error?.details?.[0]?.message ?? '' }),
      );
    }
    /* U-7 · the task sits on a licence line item, which takes no hours. */
    else if (code === 'line_item_takes_no_work') setServerError(t('error.line_item_takes_no_work'));
    /* 30 Oct 2026 · the task's contract is closed, which takes no new hour. */
    else if (code === 'contract_closed') {
      setServerError(
        t('error.contract_closed', { name: envelope?.error?.details?.[0]?.message ?? '' }),
      );
    } else if (code === 'outside_membership_window') setServerError(t('time.error.outsideWindow'));
    else if (code === 'not_permitted' || code === 'permission_denied') {
      setServerError(t('time.error.notPermitted'));
    } else if (code === 'day_over_maximum') {
      /**
       * C13. ⚠️ **The figures come from `details`, not from anything computed here.** The server
       * decided the day was full; recomputing the total client-side to word the message would give
       * two numbers that can disagree, and the one on screen would be the wrong one.
       */
      setServerError(
        t('time.error.dayFull', {
          total: detailFor(envelope, 'day_total'),
          max: detailFor(envelope, 'day_maximum'),
        }),
      );
    } else if (code === 'outside_backdate_window') {
      setServerError(t('time.error.tooFarBack', { earliest: detailFor(envelope, 'earliest') }));
    } else if (code === 'month_closed') {
      setServerError(t('time.error.monthClosed', { day: detailFor(envelope, 'closed_month') }));
      /* ⚠️ An approved claim freezes its hour — `worklog.service.ts` argues it at the error, and
         the pop-over stops offering the edit, so reaching this means another tab or a stale page. */
    } else if (code === 'overtime_approved') {
      setServerError(t('time.error.overtimeApproved'));
    } else if (code === 'validation_failed') setServerError(t('time.error.hours'));
    else if (code === 'conflict') setServerError(t('error.conflict'));
    else if (code === 'not_found') setServerError(t('error.not_found'));
    else setServerError(t('error.unavailable'));
  };

  /**
   * ⚠️ **The same write, returning the created row's id** — item 19 needs it, because a claim
   * points at a worklog and has to be told which one. `time-route.tsx` has the same pair for the
   * same reason.
   */
  const writeJson = async (
    path: string,
    init: RequestInit,
  ): Promise<{ readonly id: string; readonly billsNowhere: boolean } | null> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (!response?.ok) {
      await reportRefusal(response);
      return null;
    }
    const body = (await response.json().catch(() => null)) as { id?: string } | null;
    await reload();
    return typeof body?.id === 'string' ? { id: body.id, billsNowhere: billsNowhere(body) } : null;
  };

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (!response?.ok) {
      await reportRefusal(response);
      return false;
    }
    await reload();
    return true;
  };

  const patch = (entry: Worklog, body: Record<string, unknown>): Promise<boolean> =>
    write(`/worklogs/${entry.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...body, version: entry.version }),
    });

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <LogsScreen
          loading={loading}
          listScope={listScope}
          entries={entries}
          weekStart={weekStart}
          weekEntries={weekEntries}
          weekTotals={weekTotals}
          weekPage={weekPages.count > 1 ? pagerOf(weekPages) : undefined}
          today={today}
          projects={projects}
          addableTasks={tasks}
          search={search}
          contract={contract}
          onClearContract={() => {
            setContract(null);
          }}
          projectId={projectId}
          memberId={memberId}
          members={members}
          range={range}
          page={{
            hasPrevious: pages.hasPrevious,
            hasNext: pages.hasNext,
            onPrevious: pages.previous,
            onNext: pages.next,
            /* ⚠️ Item 20's `[<] [selector] [>] [total]`. The total counts what the filters left. */
            index: pages.index,
            count: pages.count,
            total: pages.total,
            onGoTo: pages.goTo,
          }}
          sort={sort}
          onSortChange={(next) => {
            pages.reset();
            setSort(next);
          }}
          canLog={permissions.has('worklog.create')}
          canEdit={permissions.has('worklog.update')}
          rules={rules}
          mayOverride={permissions.has('worklog.lock')}
          serverError={serverError}
          extraTaskIds={extraTaskIds}
          /* ⚠️⚠️ **EVERY FILTER RESETS THE PAGE.** An offset into the set the old filter produced
             points past the end of the new, shorter one — and an empty page reads as *"nothing
             matches"* rather than as a bug. `state-rules.md` §6. */
          onSearch={(value) => {
            pages.reset();
            setSearch(value);
          }}
          onProject={(value) => {
            pages.reset();
            setProjectId(value);
          }}
          onMember={(value) => {
            setMemberId(value);
            pages.reset();
          }}
          teams={teams}
          teamId={teamId}
          onTeam={(value) => {
            setTeamId(value);
            pages.reset();
          }}
          onRange={(value) => {
            pages.reset();
            setRange(value);
          }}
          onShiftWeek={(weeks) => {
            setWeekStart((current) => addWireDays(current, weeks * 7) ?? current);
          }}
          onOpenTask={(entry) => {
            go(`/tasks/${entry.taskKey}`);
          }}
          onDuplicate={(entry) => {
            void (async () => {
              /* ⚠️ Today, not the entry's own day — the mockup's *"Duplikálás mára"*. Duplicating
               * onto the same day would produce two identical rows nobody can tell apart. */
              const ok = await write('/worklogs', {
                method: 'POST',
                body: JSON.stringify({
                  taskId: entry.taskId,
                  workedOn: today,
                  hours: entry.hours,
                  note: entry.note,
                }),
              });
              if (ok) {
                toast({
                  title: t('logs.toast.duplicated'),
                  description: `${entry.taskKey} · ${formatHours(entry.hours)}`,
                  variant: 'success',
                });
              }
            })();
          }}
          onExport={() => {
            exporter.open('worklog');
          }}
          onImport={() => {
            importer.open('worklog');
          }}
          /* C48 · the ids of hours that already carry a claim, so the row menu can leave the
             item off rather than offer something that will be refused. */
          overtimeClaimed={overtimeClaimed}
          onMarkOvertime={(entry) => {
            void (async () => {
              const response = await session
                .request('/overtime', {
                  method: 'POST',
                  body: JSON.stringify({ worklogId: entry.id }),
                })
                .catch(() => null);

              if (response?.ok === true) {
                setToasts((current) => [
                  ...current,
                  {
                    id: `ot-${String(Date.now())}`,
                    variant: 'success',
                    title: t('logs.overtime.raised'),
                  },
                ]);
                await reload();
                return;
              }

              const body = (await response?.json().catch(() => null)) as {
                error?: { code?: string };
              } | null;
              /* ⚠️ The two refusals somebody can act on get their own sentence: one is *open the
                 claim you already have*, the other is *an administrator has to add a type*. */
              setToasts((current) => [
                ...current,
                {
                  id: `ot-${String(Date.now())}`,
                  variant: 'error',
                  title:
                    body?.error?.code === 'already_claimed'
                      ? t('logs.overtime.error.already')
                      : body?.error?.code === 'no_type'
                        ? t('logs.overtime.error.noType')
                        : t('error.unavailable'),
                },
              ]);
            })();
          }}
          onDelete={(entry) => {
            void (async () => {
              const ok = await write(`/worklogs/${entry.id}`, { method: 'DELETE' });
              if (ok) {
                toast({
                  title: t('time.toast.deleted'),
                  description: `${entry.taskKey} · ${formatHours(entry.hours)}`,
                  variant: 'success',
                });
              }
            })();
          }}
          onDeleteMany={(ids) => {
            void (async () => {
              /**
               * ⚠️⚠️ **ONE REQUEST NOW, AND REPORTED BY WHAT ACTUALLY HAPPENED.** This was N
               * requests in a loop with a note saying there was no bulk endpoint; item 72 built
               * one. The honesty is unchanged and was the right half: some rows are refused while
               * others succeed — the month lock, the backdating window, a colleague's edit — and a
               * toast saying *"12 deleted"* after nine went through is the lie `state-rules.md` §5
               * is about. The count is of `done`, which the server reports per row.
               */
              const result = await postBulk(session, '/worklogs/bulk-delete', {
                targets: bulkTargets(entries, ids),
              });
              await reload();
              if (result === null) {
                toast({ title: t('error.unavailable'), variant: 'error' });
                return;
              }
              toast({
                title: t('logs.toast.deletedMany', { count: result.done.length }),
                ...(result.failed.length === 0
                  ? {}
                  : { description: t('logs.toast.deletedSome', { total: ids.length }) }),
                variant: result.failed.length === 0 ? 'success' : 'error',
              });
            })();
          }}
          onEditHours={(entry, hours) => {
            void patch(entry, { hours });
          }}
          onEditNote={(entry, note) => {
            void patch(entry, { note: note.trim() === '' ? null : note.trim() });
          }}
          onEditDay={(entry, day) => {
            void patch(entry, { workedOn: day });
          }}
          onLogAt={(taskId, day, hours, note) => {
            void write('/worklogs', {
              method: 'POST',
              /* ⚠️ The note only where there is one — the grid's cells and the range tools send
                 none, and an empty string is a note somebody wrote rather than the absence of
                 one. Round 5 item 32 added the argument for the cell modal's add row. */
              body: JSON.stringify({
                taskId,
                workedOn: day,
                hours,
                ...(note === undefined || note.trim() === '' ? {} : { note: note.trim() }),
              }),
            });
          }}
          /**
           * ┌────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ ITEM 17 · WHAT A COPIED OR CLEARED SPAN MEANS, DAY BY DAY.             │
           * └────────────────────────────────────────────────────────────────────┘
           *
           * The grid asks for *"this task's hours on these days"* and the answer differs per day,
           * which is why the component does not attempt it: a day already holding an entry is
           * EDITED, an empty one gets a new entry, and `0` DELETES whatever is there. Three verbs,
           * one gesture.
           *
           * ⚠️⚠️ **SEQUENTIAL, AND DELIBERATELY NOT `Promise.all`.** Every one of these goes
           * through the same day-maximum, backdating-window and month-lock checks a single edit
           * does, and the server answers per request. Fired together, five refusals race to set one
           * error strip and the reader sees whichever lost; in order, the first refusal stops the
           * pass and the strip names the day that failed — which is the one they need to fix.
           *
           * ⚠️ **A cell holding more than one entry is skipped on a CLEAR** for the reason the
           * component gives about typing into a sum: deleting *the day* would silently take a note
           * with it, and the entries are individually deletable from the cell's own menu.
           */
          onSetRange={(taskKey, days, hours) => {
            void (async () => {
              for (const day of days) {
                const inCell = weekEntries.filter(
                  (entry) => entry.taskKey === taskKey && entry.workedOn === day,
                );

                if (hours === '0') {
                  if (inCell.length !== 1) continue;
                  const only = inCell[0];
                  if (only === undefined) continue;
                  if (!(await write(`/worklogs/${only.id}`, { method: 'DELETE' }))) return;
                  continue;
                }

                if (inCell.length > 1) continue;
                const existing = inCell[0];
                const ok =
                  existing === undefined
                    ? await (async () => {
                        const task = weekEntries.find((entry) => entry.taskKey === taskKey);
                        const taskId =
                          task?.taskId ?? tasks.find((candidate) => candidate.key === taskKey)?.id;
                        if (taskId === undefined) return true;
                        return write('/worklogs', {
                          method: 'POST',
                          body: JSON.stringify({ taskId, workedOn: day, hours }),
                        });
                      })()
                    : await write(`/worklogs/${existing.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ hours, version: existing.version }),
                      });
                if (!ok) return;
              }
            })();
          }}
          onAddRow={(taskId) => {
            setExtraTaskIds((current) =>
              current.includes(taskId) ? current : [...current, taskId],
            );
            const picked = tasks.find((task) => task.id === taskId);
            if (picked !== undefined) {
              setAddedTasks((current) =>
                current.some((task) => task.id === taskId) ? current : [...current, picked],
              );
            }
          }}
          onAddableQuery={searchAddable}
          onRemoveRow={(taskId) => {
            setExtraTaskIds((current) => current.filter((id) => id !== taskId));
          }}
          onCreateRange={(rangeToLog: CalendarRange) => {
            /* ⚠️ The drag's range collapses to a start and a length at THIS edge — item 20, and
               `entry-dialog.tsx` argues why it happens here rather than in the dialog. */
            setDraft(draftFromRange(rangeToLog.day, rangeToLog.from, rangeToLog.to, parseMinutes));
          }}
          onOpenEntry={(entry) => {
            setDraft(draftFromEntry(entry, formatHours(entry.hours)));
          }}
          onBlocked={(reason) => {
            setServerError(reason);
          }}
        />
      </ScreenState>

      <EntryDialog
        draft={draft}
        metafields={metafields}
        /* ⚠️ The COUNT, not the list — `0147`. `time-route.tsx` says why. */
        overtimeOffered={overtimeTypes.length > 0}
        results={taskResults}
        onSearchTasks={(query) => {
          void searchTasks(query);
        }}
        canDelete={permissions.has('worklog.update')}
        onClose={() => {
          setDraft(null);
        }}
        onSave={(saved, hours) => {
          void (async () => {
            /* ⚠️ The same body every logging form sends — item 20. Nothing downstream of this
               point knows a calendar exists. */
            const body = { taskId: saved.taskId, ...worklogBody(saved, hours) };

            const created =
              saved.id === undefined
                ? await writeJson('/worklogs', { method: 'POST', body: JSON.stringify(body) })
                : await write(`/worklogs/${saved.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ ...body, version: saved.version }),
                  });
            if (created === null || created === false) return;

            /**
             * ⚠️⚠️ **TWO WRITES, AND THE ORDER IS THE POINT** — `time-route.tsx` argues it in
             * full and this is the same pair. Item 19 is what brings it here: the calendar could
             * not mark an hour as overtime at all, and a claim points at a worklog, so the hour has
             * to exist first.
             */
            if (saved.overtime && typeof created !== 'boolean') {
              await write('/overtime', {
                method: 'POST',
                /* ⚠️ The worklog and nothing else — `0147`. */
                body: JSON.stringify({ worklogId: created.id }),
              });
            }

            setDraft(null);
            /* ⚠️ OS-1 · a new hour that bills to no line item is saved and SAID, never refused. */
            const nowhere = typeof created !== 'boolean' && created.billsNowhere;
            toast({
              title:
                saved.id === undefined
                  ? t('logs.toast.logged', { hours: formatHours(hours) })
                  : t('time.toast.changed'),
              description: nowhere
                ? t('worklog.unidentified')
                : `${saved.workedOn} · ${saved.startedAt}`,
              variant: nowhere ? 'info' : 'success',
            });
          })();
        }}
        onDelete={(id) => {
          void (async () => {
            const ok = await write(`/worklogs/${id}`, { method: 'DELETE' });
            if (ok) {
              setDraft(null);
              toast({ title: t('time.toast.deleted'), variant: 'success' });
            }
          })();
        }}
      />

      {exporter.dialog}
      {importer.dialog}

      <Toaster
        toasts={[...toasts, ...exporter.toasts, ...importer.toasts]}
        label={t('time.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
          exporter.dismiss(id);
          importer.dismiss(id);
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

/**
 * ⚠️⚠️ **IDŐRÖGZÍTÉS › IDŐLOGOK — MY OWN HOURS.** Reached from the time section's header dropdown,
 * which is the sibling of *Áttekintés*: both answer *what did I do*, one as a week and one as a
 * list. It showed everybody's until 20 Sep, so opening your own time section gave you a page of
 * other people's names.
 */
export function LogsRoute() {
  return <Logs listScope="mine" />;
}

/**
 * ⚠️⚠️ **WORKSPACE › IDŐLOGOK — EVERYBODY'S.** A different question, asked from the group where the
 * other things a reader administers already live, exactly as the workspace-wide *Kérelmek* list
 * sits apart from *Saját kérelmeim*.
 *
 * ⚠️ **What it can actually show is the server's business.** Somebody holding `worklog.read` at
 * `own` scope sees their own hours here, because `worklog.service.ts` narrows every read to the
 * caller — this address asks a wider question and is answered with whatever the asker may see.
 */
export function WorkspaceLogsRoute() {
  return <Logs listScope="all" />;
}
